// src/components/weeklyReport/AddPatientModal.js
//
// Add (or edit) one patient in a study's weekly list: find them in RegX (or
// type a patient who isn't in RegX), pick ONE status, and a subject number
// (required for Enrolled/Randomized, optional otherwise). Everything typed is kept in the autosaved draft as it changes, so
// closing this pop-up by accident loses nothing — reopening it picks up where
// the coordinator left off. Never creates or changes a patient record itself;
// "Add to RegX" (next to "not in RegX") hands off to AddToRegxModal.

import { useMemo, useState } from "react";
import { UserPlus, UserSearch, Pencil, Info } from "lucide-react";
import PatientTypeahead from "./PatientTypeahead";
import { WrModal, BTN_PRIMARY, BTN_SOFT, BTN_SMALL, INPUT, LABEL } from "./ui";
import { REPORT_STATUSES, newEntryKey, validateEntry } from "../../services/weeklyReportModel";
import { sameMrnNumber } from "../../services/patientDuplicateMatch";
import { normalizeMrn9 } from "../../services/mrn";

export const blankEntry = () => ({
  key: newEntryKey(),
  patientRef: null,
  name: "",
  dob: "",
  mrn: "",
  subjectNumber: "",
  status: "",
  notInRegx: false,
});

const AddPatientModal = ({
  open,
  study,
  entry,
  editing = false,
  onChange,
  onAdd,
  onDiscard,
  onClose,
  index,
  loading,
  sheetError,
  onAddToRegx, // open "Add this patient to RegX" (AddToRegxModal)
  notice, // e.g. "Added to RegX in Lung Center Referrals."
}) => {
  const [showErrors, setShowErrors] = useState(false);
  const current = entry || blankEntry();
  const update = (patch) => onChange({ ...current, ...patch });
  const others = (study?.patients || []).filter((p) => p.key !== current.key);
  const errors = validateEntry(current, others);
  const chosen = !!current.patientRef || current.notInRegx;

  // A hand-typed MRN that already belongs to someone in RegX: offer that record.
  const mrnMatch = useMemo(() => {
    if (!current.notInRegx || String(current.mrn || "").replace(/\D/g, "").length < 3) return null;
    const hit = (index || []).find((r) => r.patient?.mrn && sameMrnNumber(r.patient.mrn, current.mrn));
    return hit ? hit.patient : null;
  }, [current.notInRegx, current.mrn, index]);

  const add = () => {
    if (errors.length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    // Hand-typed MRNs are saved in the portal's 9-digit form.
    onAdd(current.notInRegx ? { ...current, mrn: normalizeMrn9(current.mrn).mrn9 || current.mrn } : current);
  };

  return (
    <WrModal
      open={open}
      onClose={() => {
        setShowErrors(false);
        onClose();
      }}
      icon={UserPlus}
      title={editing ? "Edit patient" : "Add a patient"}
      subtitle={study ? `${study.studyId ? `${study.studyId} – ` : ""}${study.name}` : ""}
      footer={
        <>
          {entry && (chosen || current.status || current.subjectNumber) && (
            <button
              type="button"
              onClick={() => {
                setShowErrors(false);
                onDiscard();
              }}
              className={`${BTN_SOFT} mr-auto`}
            >
              {editing ? "Cancel changes" : "Discard"}
            </button>
          )}
          <button type="button" onClick={add} className={BTN_PRIMARY} disabled={!chosen}>
            {editing ? "Save changes" : "Add patient"}
          </button>
        </>
      }
    >
      {/* 1. Who */}
      {!chosen && (
        <div>
          <span className={LABEL}>Find the patient in RegX</span>
          <PatientTypeahead
            index={index}
            loading={loading}
            sheetError={sheetError}
            onPick={(p) =>
              update({
                patientRef: { source: p.source, id: p.id },
                name: p.name || "",
                dob: p.dob || "",
                mrn: p.mrn || "",
                notInRegx: false,
              })
            }
          />
          <div className="mt-3 flex items-center gap-2">
            <span className="text-[11px] text-slate-400">Can't find them?</span>
            <button type="button" className={BTN_SMALL} onClick={() => update({ notInRegx: true, patientRef: null })}>
              <UserSearch className="w-3 h-3" />
              Add a patient who isn't in RegX
            </button>
          </div>
        </div>
      )}

      {notice && current.patientRef && (
        <p className="regx-add-done mb-2 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-200">
          {notice}
        </p>
      )}
      {current.patientRef && (
        <div className="weekly-report-picked flex items-start gap-3 rounded-xl border border-purple-400/30 bg-purple-500/10 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white truncate">{current.name || "(no name)"}</div>
            <div className="text-[11px] text-slate-400">
              MRN {current.mrn || "— (not on their record)"} · DOB {current.dob || "— (not on their record)"}
            </div>
          </div>
          {!editing && (
            <button
              type="button"
              className={BTN_SMALL}
              onClick={() => update({ patientRef: null, name: "", dob: "", mrn: "", notInRegx: false })}
            >
              <Pencil className="w-3 h-3" />
              Change
            </button>
          )}
        </div>
      )}

      {current.notInRegx && (
        <div>
          <div className="weekly-report-warn mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">
            <Info className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1 min-w-0">
              This only goes on your report, marked "not in RegX". Or add them to RegX now, in the box you pick.
            </span>
            {onAddToRegx && (
              <button type="button" className={`${BTN_SMALL} regx-add-open`} onClick={onAddToRegx}>
                <UserPlus className="w-3 h-3" />
                Add to RegX
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block sm:col-span-3">
              <span className={LABEL}>Patient name</span>
              <input
                value={current.name}
                onChange={(e) => update({ name: e.target.value })}
                className={INPUT}
                placeholder="First and last name"
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className={LABEL}>Date of birth</span>
              <input
                type="date"
                value={current.dob}
                onChange={(e) => update({ dob: e.target.value })}
                className={INPUT}
              />
            </label>
            <label className="block sm:col-span-2">
              <span className={LABEL}>MRN</span>
              <input
                value={current.mrn}
                onChange={(e) => update({ mrn: e.target.value })}
                className={INPUT}
                placeholder="Up to 9 digits"
                inputMode="numeric"
                autoComplete="off"
              />
            </label>
          </div>
          {mrnMatch && (
            <div className="weekly-report-warn mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">
              <span className="flex-1 min-w-0">
                This MRN is already in RegX: {mrnMatch.name || "(no name)"}, DOB {mrnMatch.dob || "—"}.
              </span>
              <button
                type="button"
                className={BTN_SMALL}
                onClick={() =>
                  update({
                    patientRef: { source: mrnMatch.source, id: mrnMatch.id },
                    name: mrnMatch.name || "",
                    dob: mrnMatch.dob || "",
                    mrn: mrnMatch.mrn || "",
                    notInRegx: false,
                  })
                }
              >
                Use that patient
              </button>
            </div>
          )}
          {!editing && (
            <button
              type="button"
              className={`${BTN_SMALL} mt-2`}
              onClick={() => update({ notInRegx: false, name: "", dob: "", mrn: "" })}
            >
              <UserSearch className="w-3 h-3" />
              Search RegX instead
            </button>
          )}
        </div>
      )}

      {/* 2. Status + subject number */}
      {chosen && (
        <div className="mt-4">
          <span className={LABEL}>Status this week (pick one)</span>
          <div className="space-y-1.5" role="radiogroup" aria-label="Status">
            {REPORT_STATUSES.map((s) => {
              const on = current.status === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => update({ status: s.id })}
                  className={`weekly-report-option w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-xl border transition ${
                    on
                      ? "weekly-report-option--on border-purple-400/50 bg-purple-500/15"
                      : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                  }`}
                >
                  <span
                    className={`weekly-report-radio mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${
                      on ? "weekly-report-radio--on border-purple-400 bg-purple-400" : "border-slate-500"
                    }`}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-white">{s.label}</span>
                    <span className="block text-[11px] text-slate-400">{s.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            Pick where the patient ended up this week. Someone screened and screen-failed this week is just "Screen
            fail".
          </p>

          <label className="block mt-4">
            <span className={LABEL}>
              Subject number {current.status === "enrolled" ? "(required for Enrolled/Randomized)" : "(optional)"}
            </span>
            <input
              value={current.subjectNumber}
              onChange={(e) => update({ subjectNumber: e.target.value })}
              className={INPUT}
              placeholder={current.status === "enrolled" ? "Required" : "If they have one"}
              aria-required={current.status === "enrolled"}
              autoComplete="off"
            />
          </label>
        </div>
      )}

      {showErrors && errors.length > 0 && (
        <ul className="weekly-report-error mt-4 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200 list-disc pl-6">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </WrModal>
  );
};

export default AddPatientModal;
